"use client";

import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { GoogleBrand } from "../icons/google-brand";
import { KratosFlow, KratosRegistrationResponse } from "@/lib/types/kratos";
import { FEATURES } from "@/lib/config/features";

export function GoogleSignupButton() {
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  if (!FEATURES.ENABLE_GOOGLE_AUTH) return null;

  const handleGoogleSignup = async () => {
    setIsGoogleLoading(true);

    try {
      const frontendUrl =
        process.env.NEXT_PUBLIC_APP_ENV === "production"
          ? "https://home.proc360.app/update-user"
          : "https://user-staging.proc360.app/update-user";

      const kratosBaseUrl =
        process.env.NEXT_PUBLIC_KRATOS_BASE_URL || "https://test.proc360.app/.ory/kratos/public";

      const flowRes = await fetch(
        `${kratosBaseUrl}/self-service/registration/browser?return_to=${encodeURIComponent(
          frontendUrl
        )}`,
        {
          method: "GET",
          headers: { Accept: "application/json" },
          credentials: "include",
        }
      );

      if (!flowRes.ok) {
        throw new Error("Failed to initialize registration flow");
      }

      const flow = (await flowRes.json()) as KratosFlow;

      const csrfToken =
        flow.ui?.nodes?.find((node) => node.attributes?.name === "csrf_token")?.attributes?.value || "";

      const submitRes = await fetch(`${kratosBaseUrl}/self-service/registration?flow=${flow.id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          method: "oidc",
          provider: "google",
          csrf_token: csrfToken,
        }),
      });

      const submitData = (await submitRes.json()) as KratosRegistrationResponse & {
        error?: {
          id?: string;
          code?: number;
          status?: string;
          reason?: string;
          message?: string;
          redirect_browser_to?: string;
        };
      };

      const redirectTo =
        submitData.redirect_browser_to ||
        submitData.error?.redirect_browser_to ||
        submitData.continue_with?.find((c) => c.action === "redirect_browser_to")?.redirect_browser_to;

      if (redirectTo) {
        window.location.href = redirectTo;
      } else {
        throw new Error(submitData.error?.message || "Could not retrieve redirect destination from Kratos");
      }
    } catch (e) {
      console.error(e);
      toast.error(e instanceof Error ? e.message : "Something went wrong");
      setIsGoogleLoading(false);
    }
  };

  return (
    <Button
      className="w-full border-primary-300 text-primary hover:text-primary-400 bg-white hover:bg-primary-50/20 h-[44px]"
      size="lg"
      variant="outline"
      radius="full"
      type="button"
      onClick={handleGoogleSignup}
      disabled={isGoogleLoading}
    >
      {isGoogleLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <GoogleBrand fontSize={24} />}
      <span>{isGoogleLoading ? "Connecting to Google..." : "Sign up with Google"}</span>
    </Button>
  );
}